import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useFormik } from "formik";
import Container from "react-bootstrap/Container";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Stack from "react-bootstrap/Stack";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFilter,
  faSearch,
  faRotateRight,
  faStore,
  faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";

import { formatPostcode } from "../helpers/postcodeHelper";
import { MainLayout } from "../layouts/mainLayout";
import { LocationViewModel, MachineViewModel, SearchLocationsBindingModel } from "../apiClient";
import { useApiClient } from "../helpers/useApiClient";
import { LocationResult } from "../components/locationResult";
import AppSpinner from "../components/appSpinner";
import { Error } from "../routes/error";

type ResultsRouteParams = {
  postcode: string;
};

export const Results = () => {
  const [error, setError] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState(false);
  const [machines, setMachines] = useState<MachineViewModel[] | null>(null);
  const [locations, setLocations] = useState<LocationViewModel[] | null>(null);
  const params = useParams<ResultsRouteParams>();
  const apiClient = useApiClient();

  const formattedPostcode = formatPostcode(params.postcode || "");

  const { handleChange, handleSubmit, values, setSubmitting, isSubmitting, submitForm, resetForm } =
    useFormik({
      initialValues: {
        postcode: formattedPostcode,
        machines: [] as string[],
        query: "",
      },
      onSubmit: async (formValues) => {
        try {
          const searchResults = await apiClient.search(
            new SearchLocationsBindingModel({
              postcode: formValues.postcode,
              machines: formValues.machines,
              query: formValues.query,
            })
          );
          setLocations(searchResults.results || []);
          setSubmitting(false);
        } catch (err) {
          console.error(err);
          setError(true);
        }
      },
    });

  useEffect(() => {
    const loadMachines = async () => {
      setSubmitting(true);
      try {
        const machinesData = await apiClient.machines();
        setMachines(machinesData);
        submitForm();
      } catch (err) {
        console.error(err);
        setError(true);
      }
    };

    if (machines) return;
    loadMachines();
  }, [apiClient, machines, setSubmitting, submitForm]);

  const handleClearFilters = () => {
    resetForm({
      values: {
        postcode: formattedPostcode,
        machines: [],
        query: "",
      },
    });
    submitForm();
  };

  const hasActiveFilters = values.machines.length > 0 || values.query.trim().length > 0;

  return (
    <>
      {error && <Error />}
      {!error && (
        <MainLayout title={`Search results for ${values.postcode}`}>
          <Container className="py-4">
            <Link to="/" className="kf-back-link">
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back to search</span>
            </Link>

            <header className="mb-4">
              <h1 className="h2 mb-1">Keg retailers near {values.postcode}</h1>
              <p className="text-secondary mb-0">
                {locations && !isSubmitting
                  ? `${locations.length} ${locations.length === 1 ? "retailer" : "retailers"} within 50 miles`
                  : "Searching within 50 miles"}
              </p>
            </header>

            <Form onSubmit={handleSubmit}>
              <Row className="g-4">
                {/* Filters: collapsible below lg, always visible on lg+ */}
                <Col lg={3}>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    className="d-lg-none w-100 mb-3"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-expanded={showFilters}
                    aria-controls="kf-filters"
                  >
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    {showFilters ? "Hide filters" : "Show filters"}
                    {hasActiveFilters && <Badge bg="primary" className="ms-2">On</Badge>}
                  </Button>

                  <Card id="kf-filters" as="aside" className={`kf-filter-card${showFilters ? "" : " d-none"} d-lg-block`}>
                    <Card.Header as="h2" className="h6 mb-0">Filter results</Card.Header>
                    <Card.Body>
                      <Stack gap={3}>
                        <Form.Group controlId="query">
                          <Form.Label>Name or town</Form.Label>
                          <Form.Control
                            type="text"
                            name="query"
                            placeholder="e.g. Morrisons"
                            onChange={handleChange}
                            value={values.query}
                          />
                        </Form.Group>

                        {machines && (
                          <Form.Group id="machines">
                            <Form.Label>Machine</Form.Label>
                            {machines.map((machine) => (
                              <Form.Check
                                key={machine.id}
                                type="checkbox"
                                name="machines"
                                id={`machine-${machine.id}`}
                                label={machine.name}
                                value={machine.id}
                                checked={values.machines?.includes(machine.id!) ?? false}
                                onChange={(ev) => {
                                  handleChange(ev);
                                  submitForm();
                                }}
                              />
                            ))}
                          </Form.Group>
                        )}

                        <div className="d-grid gap-2">
                          <Button variant="primary" type="submit" disabled={isSubmitting}>
                            <FontAwesomeIcon icon={faSearch} className="me-2" />
                            Search
                          </Button>
                          {hasActiveFilters && (
                            <Button variant="outline-secondary" type="button" onClick={handleClearFilters}>
                              <FontAwesomeIcon icon={faRotateRight} className="me-2" />
                              Reset
                            </Button>
                          )}
                        </div>
                      </Stack>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Results List */}
                <Col lg={9}>
                  {locations && !isSubmitting && (
                    <>
                      {locations.length === 0 && (
                        <Alert variant="danger" className="p-4 text-center">
                          <div className="fs-1 mb-3">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                          </div>
                          <Alert.Heading>No retailers found matching your search</Alert.Heading>
                          <p className="text-muted mb-4">
                            We couldn't find any stockists within 50 miles of {values.postcode}{" "}
                            with the selected filters.
                          </p>
                          <div className="d-flex justify-content-center gap-3 flex-wrap">
                            {hasActiveFilters && (
                              <Button
                                variant="outline-light"
                                size="sm"
                                onClick={handleClearFilters}
                              >
                                Clear filters
                              </Button>
                            )}
                            <Link to="/submit-retailer" className="btn btn-primary btn-sm fw-semibold">
                              <FontAwesomeIcon icon={faStore} className="me-2" />
                              Submit a retailer
                            </Link>
                          </div>
                        </Alert>
                      )}

                      <Stack gap={4}>
                        {locations.map((location) => (
                          <LocationResult location={location} key={location.id} />
                        ))}
                      </Stack>
                    </>
                  )}

                  {(!locations || isSubmitting) && <AppSpinner />}
                </Col>
              </Row>
            </Form>
          </Container>
        </MainLayout>
      )}
    </>
  );
};